import type {
  AddedConcept,
  AgentConcept,
  AgentConceptsSnapshot,
} from '../state/createProjectSlice';

// TODO: cleanup with cp_annotation_createproject_agent ff

/**
 * Reconciles the builder's concepts with the step 2 concept groups, keyed by
 * `conceptGroupId` because a step 2 card can swap its group.
 *
 * - A group not yet in the builder gets `nextSequence` and its step 2 name.
 * - A group no longer in step 2 is dropped; its sequence is never reused.
 * - A group that stays keeps its saved edits.
 *
 * Returns `current` itself when nothing changed, so callers can skip the dispatch.
 */
export const mergeAgentConcepts = (
  addedConcepts: AddedConcept[],
  current: AgentConceptsSnapshot
): AgentConceptsSnapshot => {
  const step2Names = new Map<string, string>();
  addedConcepts.forEach(({ conceptGroupId, conceptGroupName }) => {
    const name = conceptGroupName.trim();
    // A card whose group is not chosen yet has no ID to key on.
    if (conceptGroupId && name && !step2Names.has(conceptGroupId)) {
      step2Names.set(conceptGroupId, name);
    }
  });

  const concepts: Record<string, AgentConcept> = {};
  let nextSequence = current.nextSequence;
  let hasAddition = false;
  step2Names.forEach((name, conceptGroupId) => {
    const saved = current.concepts[conceptGroupId];
    if (saved) {
      concepts[conceptGroupId] = saved;
      return;
    }
    concepts[conceptGroupId] = { sequence: nextSequence, name, synonyms: [], notes: '' };
    nextSequence += 1;
    hasAddition = true;
  });

  // Every retained group comes from `current`, so equal sizes without additions
  // means the same set of groups.
  const hasRemoval = Object.keys(current.concepts).length !== Object.keys(concepts).length;
  return hasAddition || hasRemoval ? { concepts, nextSequence } : current;
};
